import { Link } from "react-router-dom";
import { LegalPage } from "@/components/LegalPage";
import { Placeholder } from "@/components/Placeholder";
import { BUSINESS, CONTACT } from "@/data/content";

const P = ({ children }) => <p className="mb-3 last:mb-0">{children}</p>;

const SECTIONS = [
  {
    heading: "Who We Are",
    body: (
      <>
        <P>
          These Terms & Conditions govern the services provided by {CONTACT.name} (legal name:{" "}
          <Placeholder field={BUSINESS.legalName} />, business type: <Placeholder field={BUSINESS.businessType} />), operating
          from {CONTACT.address}, {CONTACT.country} ("we", "us"). You can reach us at {CONTACT.email} or {CONTACT.phone}.
        </P>
        <P>By requesting a quotation, accepting a quotation, or using our services, you ("the Client") agree to these Terms.</P>
      </>
    ),
  },
  {
    heading: "Services Provided",
    body: (
      <P>
        We are a business-to-business consultancy. We provide website development services (business website design, responsive development, landing pages, corporate
        websites, redesign, maintenance, performance improvements, basic SEO setup, and contact/lead-generation functionality);
        business management services (business process consulting, operations support, workflow planning, documentation and
        organisation, digital business setup assistance, and website/content coordination); SEO/SMO services; and digital
        marketing services (Google Ads management and social media marketing). The specific services for any engagement are
        those described in the written quotation accepted by the Client. We do not offer remote IT support, computer
        repair or consumer technical assistance, and no such service is provided under these Terms.
      </P>
    ),
  },
  {
    heading: "Quotations and Orders",
    body: (
      <>
        <P>
          All work is undertaken on the basis of a written quotation issued by us and accepted by the Client in writing (including
          by email). A quotation describes the scope, deliverables, exclusions, delivery timeframe, price, payment terms and
          revision allowance. Quotations are valid for 3 days from the date of issue unless stated otherwise.
        </P>
        <P>No contract exists until we have received the Client's written acceptance and any deposit stated in the quotation.</P>
      </>
    ),
  },
  {
    heading: "Pricing and Payment Terms",
    body: (
      <>
        <P>
          Prices are stated in {BUSINESS.currency.value} and exclude any applicable taxes unless the quotation states otherwise.
          For one-off projects, 50% of the fee is payable as a deposit on acceptance of the quotation and the remaining 50% on
          completion, unless the quotation states otherwise. Monthly services are invoiced in advance for each service month.
        </P>
        <P>
          Invoices are payable within 3 days of the invoice date by the methods described on our{" "}
          <Link to="/payment-information" className="font-medium text-brand hover:underline">Payment Information</Link> page. We may pause work on overdue
          accounts after written notice. Third-party costs (hosting, domains, advertising spend, licences) are the Client's
          responsibility unless the quotation states otherwise.
        </P>
      </>
    ),
  },
  {
    heading: "Project Scope and Changes",
    body: (
      <P>
        The scope of work is defined by the accepted quotation. Requests outside that scope will be quoted separately before
        the work is carried out. Either party may propose a change to scope at any time; changes take effect only when agreed in writing.
      </P>
    ),
  },
  {
    heading: "Project Fulfilment",
    body: (
      <>
        <P>
          Projects are fulfilled in the stages described in the accepted quotation. For website and software work the usual
          stages are discovery, design, development, client review, revisions, and handover. For business management
          engagements the usual stages are discovery, written recommendations or plans, implementation support, and review.
          Each stage is delivered digitally and confirmed by email.
        </P>
        <P>
          A stage is treated as accepted when the Client approves it in writing, or when no feedback is received within{" "}
          <strong>10 business days</strong> of delivery. Final deliverables, access credentials and source files are released on
          receipt of the final milestone payment. Monthly retainer services are fulfilled continuously during each paid billing
          month and reported on in writing.
        </P>
      </>
    ),
  },
  {
    heading: "Client Data Handling",
    body: (
      <>
        <P>
          In the course of an engagement we may receive business information, documents, content, customer records and access
          credentials from the Client. We treat all such material as confidential. It is used only to perform the agreed work,
          is accessed only by the people who need it for that work, and is not sold, shared or used for any other purpose.
        </P>
        <P>
          Access credentials are stored securely, used only for the agreed tasks, and returned, rotated or revoked when the
          engagement ends. On completion or termination, and on written request, we return or delete Client data in our
          possession, except for records we are required to retain for accounting or legal purposes. Our{" "}
          <Link to="/privacy-policy" className="font-medium text-brand hover:underline">Privacy Policy</Link> describes how personal information is handled.
        </P>
      </>
    ),
  },
  {
    heading: "Delivery Timelines",
    body: (
      <P>
        Delivery timeframes in a quotation are estimates made in good faith and depend on the timely provision of content,
        feedback, approvals and access by the Client. Delays caused by late Client input, scope changes or third-party services
        extend the timeframe accordingly. We will inform the Client promptly of any anticipated delay.
      </P>
    ),
  },
  {
    heading: "Revisions",
    body: (
      <P>
        Each quotation states the number of revision rounds included at each stage. Revisions are changes within the agreed
        scope; new features, additional pages or changes of direction after approval are treated as scope changes and quoted separately.
      </P>
    ),
  },
  {
    heading: "Client Responsibilities",
    body: (
      <P>
        The Client agrees to provide accurate information, content and access needed for the work; to review deliverables and
        give feedback within reasonable time; to pay invoices when due; and to ensure that all content, images, data and
        credentials supplied to us are lawfully held and may be used for the agreed purpose. The Client is responsible for the
        accuracy and legality of content published on their website or profiles.
      </P>
    ),
  },
  {
    heading: "Intellectual Property",
    body: (
      <>
        <P>
          On receipt of full payment, the Client owns the deliverables created specifically for them under the engagement
          (designs, code written for the project, documents and content), excluding third-party components, which remain subject
          to their own licences. Until full payment, all such materials remain our property.
        </P>
        <P>
          We retain ownership of our pre-existing tools, templates, know-how and methods. We may reference the Client's project
          in our portfolio unless the Client asks us in writing not to.
        </P>
      </>
    ),
  },
  {
    heading: "Website, Content and Third-Party Services",
    body: (
      <P>
        Hosting, domain registration, email services, advertising platforms and other third-party services are provided by
        their respective providers under their own terms, normally in accounts held in the Client's name. We are not responsible
        for the availability, performance or policies of third-party services. Ongoing maintenance, security updates and backups
        are provided only where included in an accepted quotation.
      </P>
    ),
  },
  {
    heading: "Service Limitations",
    body: (
      <P>
        We do not guarantee search engine rankings, website traffic, leads, sales or any specific business outcome. We do not
        provide legal, tax, accounting or financial advice. Our business management services are advisory and supportive; decisions
        remain the Client's. Nothing on this website or in our deliverables should be relied upon as professional advice in those regulated fields.
      </P>
    ),
  },
  {
    heading: "Acceptable Use",
    body: (
      <P>
        We will not build, promote or support websites, content or campaigns that are unlawful, deceptive, infringing, or that
        we reasonably consider harmful. We may decline or terminate an engagement that falls into these categories, with any
        refund handled in accordance with our Refund & Cancellation Policy.
      </P>
    ),
  },
  {
    heading: "Cancellation and Refunds",
    body: (
      <P>
        Cancellation rights, notice periods, treatment of deposits and completed work, and refund eligibility are set out in our{" "}
        <Link to="/refund-policy" className="font-medium text-brand hover:underline">Refund & Cancellation Policy</Link>, which forms part of these Terms.
      </P>
    ),
  },
  {
    heading: "Limitation of Liability",
    body: (
      <P>
        To the fullest extent permitted by law, our total liability arising from an engagement is limited to the fees paid by the
        Client for that engagement. We are not liable for indirect or consequential losses, loss of profit, loss of data caused
        by third-party services, or for losses arising from Client-supplied content or late Client input. Nothing in these Terms
        limits liability that cannot be limited by law.
      </P>
    ),
  },
  {
    heading: "Governing Law and Disputes",
    body: (
      <P>
        These Terms are governed by the laws of the State of Wyoming, United States of America.
        If a dispute arises, please contact us first at {CONTACT.email} or {CONTACT.phone}; we aim to resolve concerns directly and promptly.
      </P>
    ),
  },
  {
    heading: "Changes to These Terms",
    body: <P>We may update these Terms from time to time. The version in force at the date a quotation is accepted applies to that engagement.</P>,
  },
  {
    heading: "Contact",
    body: (
      <P>
        {CONTACT.name}, {CONTACT.address}, {CONTACT.country}. Email {CONTACT.email}. Telephone {CONTACT.phone}.
      </P>
    ),
  },
];

export default function TermsConditions() {
  return (
    <LegalPage
      testId="terms-page"
      overline="Legal — Terms & Conditions"
      title="Terms & Conditions"
      lede="The terms on which My Geeks LLC provides website development, business management, SEO/SMO and digital marketing services."
      seoDescription="Terms and Conditions for services provided by My Geeks LLC, including quotations, payment, scope, delivery, revisions, intellectual property and liability."
      sections={SECTIONS}
      updated="October 2, 2026"
    />
  );
}
